import React, { useMemo } from "react";

function normalize(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function toDate(value) {
  if (!value) return null;

  try {
    const parsed =
      typeof value?.toDate === "function"
        ? value.toDate()
        : new Date(value);

    return Number.isNaN(parsed.getTime())
      ? null
      : parsed;
  } catch {
    return null;
  }
}

function formatDateTime(value) {
  const parsed = toDate(value);

  return parsed
    ? parsed.toLocaleString()
    : "Date unavailable";
}

function titleCase(value) {
  return String(value || "unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) =>
      character.toUpperCase()
    );
}

function getTone(type) {
  const tones = {
    created: {
      color: "#475569",
      background: "#f1f5f9",
      border: "#cbd5e1",
    },
    offered: {
      color: "#92400e",
      background: "#fef3c7",
      border: "#fde68a",
    },
    requested: {
      color: "#7c3aed",
      background: "#ede9fe",
      border: "#ddd6fe",
    },
    assigned: {
      color: "#1d4ed8",
      background: "#dbeafe",
      border: "#bfdbfe",
    },
    active: {
      color: "#0369a1",
      background: "#e0f2fe",
      border: "#bae6fd",
    },
    arrived: {
      color: "#0f766e",
      background: "#ccfbf1",
      border: "#99f6e4",
    },
    completed: {
      color: "#166534",
      background: "#dcfce7",
      border: "#bbf7d0",
    },
    warning: {
      color: "#b91c1c",
      background: "#fee2e2",
      border: "#fecaca",
    },
    financial: {
      color: "#6d28d9",
      background: "#f3e8ff",
      border: "#e9d5ff",
    },
  };

  return tones[type] || tones.created;
}

function getActor(shift, eventName) {
  const eventKey = String(eventName || "");

  const actorFields = {
    Created: [
      shift?.createdByEmail,
      shift?.createdByName,
      shift?.createdBy,
      shift?.createdByUid,
    ],
    Offered: [
      shift?.offeredByName,
      shift?.offeredBy,
      shift?.offeredByUid,
    ],
    Requested: [
      shift?.requestedByName,
      shift?.requestedBy,
      shift?.requestedWorkerName,
    ],
    Assigned: [
      shift?.assignedByName,
      shift?.assignedBy,
      shift?.assignedByUid,
    ],
    Activated: [
      shift?.activatedByName,
      shift?.activatedBy,
      shift?.assignedWorkerName,
    ],
    "On My Way": [
      shift?.trackingByName,
      shift?.onMyWayByName,
      shift?.assignedWorkerName,
    ],
    Arrived: [
      shift?.arrivedByName,
      shift?.assignedWorkerName,
    ],
    Completed: [
      shift?.completedByName,
      shift?.completedBy,
      shift?.assignedWorkerName,
    ],
    "No Show": [
      shift?.noShowByName,
      shift?.noShowBy,
      shift?.noShowByUid,
    ],
    Cancelled: [
      shift?.cancelledByName,
      shift?.canceledByName,
      shift?.cancelledBy,
      shift?.canceledBy,
    ],
  };

  return (
    (actorFields[eventKey] || []).find(Boolean) ||
    ""
  );
}

export default function Shift360Timeline({
  shift,
}) {
  const events = useMemo(() => {
    if (!shift) return [];

    const rows = [];

    function addEvent({
      key,
      title,
      date,
      type,
      description,
    }) {
      const parsedDate = toDate(date);

      if (!parsedDate) return;

      rows.push({
        key,
        title,
        date: parsedDate,
        type,
        description,
        actor: getActor(shift, title),
      });
    }

    addEvent({
      key: "created",
      title: "Created",
      date: shift?.createdAt,
      type: "created",
      description:
        "The shift was created and entered the scheduling lifecycle.",
    });

    addEvent({
      key: "offered",
      title: "Offered",
      date: shift?.offeredAt,
      type: "offered",
      description:
        "The shift was offered to one or more eligible workers.",
    });

    addEvent({
      key: "requested",
      title: "Requested",
      date:
        shift?.requestedAt ||
        shift?.requestSubmittedAt,
      type: "requested",
      description:
        "A worker requested or responded to the shift.",
    });

    addEvent({
      key: "assigned",
      title: "Assigned",
      date: shift?.assignedAt,
      type: "assigned",
      description: shift?.assignedWorkerName
        ? `Assigned to ${shift.assignedWorkerName}.`
        : "A worker was assigned to the shift.",
    });

    addEvent({
      key: "activated",
      title: "Activated",
      date: shift?.activatedAt,
      type: "active",
      description:
        "The assigned worker activated the shift.",
    });

    addEvent({
      key: "tracking",
      title: "On My Way",
      date:
        shift?.trackingAt ||
        shift?.onMyWayAt ||
        shift?.enRouteAt,
      type: "active",
      description:
        "The worker confirmed travel to the facility.",
    });

    addEvent({
      key: "arrived",
      title: "Arrived",
      date:
        shift?.arrivedAt ||
        shift?.checkedInAt ||
        shift?.arrivalTime,
      type: "arrived",
      description:
        "The worker confirmed arrival at the facility.",
    });

    addEvent({
      key: "completed",
      title: "Completed",
      date: shift?.completedAt,
      type: "completed",
      description:
        shift?.autoCompleted === true
          ? "The shift was automatically completed after its scheduled end."
          : "The shift was marked completed.",
    });

    addEvent({
      key: "no-show",
      title: "No Show",
      date: shift?.noShowAt,
      type: "warning",
      description:
        shift?.noShowReason ||
        "The assigned worker was marked as a no-show.",
    });

    addEvent({
      key: "cancelled",
      title: "Cancelled",
      date:
        shift?.cancelledAt ||
        shift?.canceledAt,
      type: "warning",
      description:
        shift?.cancellationReason ||
        shift?.cancelReason ||
        "The shift was cancelled.",
    });

    addEvent({
      key: "payroll",
      title: `Payroll ${titleCase(
        shift?.payrollStatus || "Updated"
      )}`,
      date:
        shift?.payrollUpdatedAt ||
        shift?.paidAt,
      type: "financial",
      description:
        "The payroll status for this shift was updated.",
    });

    addEvent({
      key: "invoice",
      title: `Invoice ${titleCase(
        shift?.invoiceStatus || "Updated"
      )}`,
      date:
        shift?.invoiceUpdatedAt ||
        shift?.invoicedAt,
      type: "financial",
      description:
        "The invoice status for this shift was updated.",
    });

    return rows.sort(
      (a, b) =>
        a.date.getTime() - b.date.getTime()
    );
  }, [shift]);

  const currentStatus = normalize(
    shift?.status ||
      shift?.lifecycleStatus ||
      "open"
  );

  return (
    <div style={{ display: "grid", gap: 13 }}>
      <div>
        <div
          style={{
            color: "#0f172a",
            fontSize: 18,
            fontWeight: 950,
          }}
        >
          Lifecycle Timeline
        </div>

        <div
          style={{
            marginTop: 4,
            color: "#64748b",
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          Recorded operational lifecycle events for this shift.
        </div>
      </div>

      <div
        style={{
          padding: 12,
          borderRadius: 13,
          border: "1px solid #dbeafe",
          background: "#eff6ff",
        }}
      >
        <div
          style={{
            color: "#64748b",
            fontSize: 9,
            fontWeight: 900,
            textTransform: "uppercase",
          }}
        >
          Current Lifecycle Status
        </div>

        <div
          style={{
            marginTop: 4,
            color: "#1d4ed8",
            fontSize: 18,
            fontWeight: 950,
          }}
        >
          {titleCase(currentStatus)}
        </div>
      </div>

      {events.length === 0 ? (
        <div
          style={{
            padding: 18,
            borderRadius: 13,
            border: "1px dashed #cbd5e1",
            background: "#f8fafc",
            color: "#64748b",
            textAlign: "center",
            fontSize: 11,
            fontWeight: 750,
          }}
        >
          No dated lifecycle events are available.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 0 }}>
          {events.map((event, index) => {
            const tone = getTone(event.type);
            const isLast =
              index === events.length - 1;

            return (
              <div
                key={event.key}
                style={{
                  display: "grid",
                  gridTemplateColumns: "32px 1fr",
                  gap: 9,
                }}
              >
                <div
                  style={{
                    display: "grid",
                    justifyItems: "center",
                    gridTemplateRows:
                      "30px minmax(0, 1fr)",
                  }}
                >
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      display: "grid",
                      placeItems: "center",
                      borderRadius: 999,
                      border: `1px solid ${tone.border}`,
                      background: tone.background,
                      color: tone.color,
                      fontSize: 13,
                      fontWeight: 950,
                    }}
                  >
                    ✓
                  </div>

                  {!isLast ? (
                    <div
                      style={{
                        width: 2,
                        minHeight: 35,
                        background: "#cbd5e1",
                      }}
                    />
                  ) : null}
                </div>

                <div
                  style={{
                    padding: "4px 0 15px",
                  }}
                >
                  <div
                    style={{
                      color: tone.color,
                      fontSize: 12,
                      fontWeight: 950,
                    }}
                  >
                    {event.title}
                  </div>

                  <div
                    style={{
                      marginTop: 3,
                      color: "#64748b",
                      fontSize: 9,
                      fontWeight: 750,
                    }}
                  >
                    {formatDateTime(event.date)}
                  </div>

                  <div
                    style={{
                      marginTop: 6,
                      color: "#475569",
                      fontSize: 10,
                      fontWeight: 700,
                      lineHeight: 1.45,
                    }}
                  >
                    {String(event.description)}
                  </div>

                  {event.actor ? (
                    <div
                      style={{
                        marginTop: 5,
                        color: "#64748b",
                        fontSize: 9,
                        fontWeight: 750,
                      }}
                    >
                      Actor: {String(event.actor)}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}